import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import { Tag } from 'primereact/tag';
import { CreateDepositPaymentItem } from '../../../../../../api/app-api';
import { AccountingDepositDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-details-entity';
import { formatDate } from '../../../../../../utils/date-format';
import { getAccountingDepositsListOperation } from '../../deposits/operations/get-accounting-deposits-list-operation';
import { addPaymentsToExistingDepositOperation } from '../operations/add-payments-to-existing-deposit-operation';

interface AddPaymentsToExistingDepositDialogProps {
  visible: boolean;
  /** Pre-computed count for the summary bar. */
  paymentsCount: number;
  /** Pre-computed total amount for the summary bar. */
  totalAmount: number;
  /** Already-mapped via toPaymentItem in the parent — sent as-is to the API. */
  payments: CreateDepositPaymentItem[];
  onHide: () => void;
  /** Parent clears its pending-payment selection and re-runs its search. */
  onSuccess: () => void;
}

export const AddPaymentsToExistingDepositDialog: React.FC<
  AddPaymentsToExistingDepositDialogProps
> = ({ visible, paymentsCount, totalAmount, payments, onHide, onSuccess }) => {
  const [deposits, setDeposits] = useState<AccountingDepositDetailsEntity[]>(
    [],
  );
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loadingDeposits, setLoadingDeposits] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedDeposit, setSelectedDeposit] =
    useState<AccountingDepositDetailsEntity | null>(null);

  const fetchDeposits = async (nextOffset: number, nextLimit: number) => {
    await getAccountingDepositsListOperation(
      nextOffset,
      nextLimit,
      setDeposits,
      setTotal,
      setLoadingDeposits,
    );
  };

  // Reset selection + paginator + reload deposits every time the dialog
  // opens, so reopening for a different pending-payment batch never carries
  // over a previous deposit pick or a stale page.
  useEffect(() => {
    if (!visible) return;
    setSelectedDeposit(null);
    setOffset(0);
    fetchDeposits(0, limit);
  }, [visible]);

  const onPageChange = async (event: DataTablePageEvent) => {
    const nextOffset = event.first;
    const nextLimit = event.rows;
    setOffset(nextOffset);
    setLimit(nextLimit);
    await fetchDeposits(nextOffset, nextLimit);
  };

  const handleClose = () => {
    if (submitting) return;
    onHide();
  };

  const handleApprove = async () => {
    if (!selectedDeposit || selectedDeposit.isLocked || submitting) return;
    await addPaymentsToExistingDepositOperation(
      selectedDeposit.id,
      payments,
      setSubmitting,
      () => {
        onSuccess();
        onHide();
      },
    );
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button
        label="ביטול"
        icon="pi pi-times"
        severity="secondary"
        text
        onClick={handleClose}
        disabled={submitting}
      />
      <Button
        label={
          submitting
            ? 'שומר...'
            : selectedDeposit
              ? `הוסף ${paymentsCount} תשלומים להפקדה #${selectedDeposit.id}`
              : 'אישור'
        }
        icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
        onClick={handleApprove}
        disabled={!selectedDeposit || selectedDeposit.isLocked || submitting}
      />
    </div>
  );

  return (
    <Dialog
      header="הוספת תשלומים להפקדה קיימת"
      visible={visible}
      onHide={handleClose}
      modal
      draggable={false}
      resizable={false}
      closable={!submitting}
      style={{ width: '1080px', maxWidth: '95vw', direction: 'rtl' }}
      footer={footer}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '4px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 12px',
            borderRadius: '6px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            color: '#065f46',
            fontWeight: 600,
            fontSize: '0.9rem',
          }}
        >
          <span>התשלומים שייווסו: {paymentsCount}</span>
          <span>|</span>
          <span>סכום: {totalAmount.toFixed(2)} ש״ח</span>
        </div>

        <div style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}>
          סה״כ הפקדות: {total.toLocaleString()}
        </div>

        <DataTable
          value={deposits}
          loading={loadingDeposits}
          rowHover
          paginator
          lazy
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו הפקדות</div>
          }
          dataKey="id"
          rowClassName={(row) =>
            row.isLocked ? 'deposit-row-disabled' : undefined
          }
          tableStyle={{ direction: 'rtl' }}
        >
          <Column
            header=""
            headerStyle={{ width: '3.5rem' }}
            style={{ width: '3.5rem', textAlign: 'center' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.isLocked ? (
                <span
                  title="הפקדה נעולה — לא ניתן לבחור"
                  aria-label="הפקדה נעולה — לא ניתן לבחור"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <i
                    className="pi pi-lock"
                    style={{ color: '#9ca3af', fontSize: '0.95rem' }}
                  />
                </span>
              ) : (
                <RadioButton
                  inputId={`deposit-select-${row.id}`}
                  checked={selectedDeposit?.id === row.id}
                  onChange={() => setSelectedDeposit(row)}
                />
              )
            }
          />
          <Column
            field="id"
            header="מזהה"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="depositDate"
            header="תאריך הפקדה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.depositDate ? formatDate(row.depositDate as Date) : 'חסר'
            }
          />
          <Column
            field="totalAmount"
            header="סכום כולל"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              typeof row.totalAmount === 'number'
                ? row.totalAmount.toFixed(2)
                : 'חסר'
            }
          />
          <Column
            field="isLocked"
            header="סטטוס"
            style={{ textAlign: 'right', width: '110px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) => (
              <Tag
                value={row.isLocked ? 'נעולה' : 'פתוחה'}
                severity={row.isLocked ? 'success' : 'warning'}
              />
            )}
          />
          <Column
            field="bankAccount.bankName"
            header="חשבון בנק"
            style={{ textAlign: 'right', width: '130px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="itemsCount"
            header="כמות פריטים"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="cashCount"
            header="מזומן"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="checkCount"
            header="המחאות"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.createdByName?.trim() ? row.createdByName : 'חסר'
            }
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
            }
          />
        </DataTable>
      </div>

      <style>{`
        .deposit-row-disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
      `}</style>
    </Dialog>
  );
};
